import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useQuery } from 'react-query';
import { Button, COLOR_SYSTEM, Text, Spinner, Input } from 'components/atoms';
import Dropdown, { DropdownMenuItem } from 'components/molecules/Dropdown';
import { StepData } from './index';
import { promptService } from 'api/promptService';
import { PromptType, AVAILABLE_AI_MODELS } from 'common/models/types';

interface ExperimentStep4Props {
  data: StepData;
  onNext: (data: Partial<StepData>) => void;
  onBack: () => void;
}


const ExperimentStep4: React.FC<ExperimentStep4Props> = ({
  data,
  onNext,
  onBack,
}) => {
  const [formData, setFormData] = useState({
    evaluationPromptId: data.evaluationPromptId,
    evaluationModel: data.evaluationModel || 'GPT_4',
    evaluationUseGTSnapshot: data.evaluationUseGTSnapshot || false,
    iterations: data.iterations || 1,
  });
  
  const [showPromptDropdown, setShowPromptDropdown] = useState(false);
  const [showModelDropdown, setShowModelDropdown] = useState(false);
  
  // 평가용 프롬프트 목록 조회 (INTERNAL_REVIEW_EVALUATION 타입)
  const { data: promptsResponse, isLoading: isLoadingPrompts } = useQuery({
    queryKey: ['prompts', PromptType.INTERNAL_REVIEW_EVALUATION],
    queryFn: () => promptService.getPrompts({
      promptType: PromptType.INTERNAL_REVIEW_EVALUATION,
    }),
  });
  
  const prompts = promptsResponse?.data || [];
  
  // 첫 번째 프롬프트를 기본값으로 설정
  useEffect(() => {
    if (prompts.length > 0 && !formData.evaluationPromptId) {
      const firstPrompt = prompts[0];
      setFormData(prev => ({
        ...prev,
        evaluationPromptId: firstPrompt.id,
        evaluationModel: firstPrompt.model || prev.evaluationModel,
      }));
    }
  }, [prompts]);
  
  // 프롬프트 선택 시 해당 프롬프트의 모델을 기본값으로 설정
  useEffect(() => {
    const selectedPrompt = prompts.find(p => p.id === formData.evaluationPromptId);
    if (selectedPrompt?.model) {
      setFormData(prev => ({ ...prev, evaluationModel: selectedPrompt.model }));
    }
  }, [formData.evaluationPromptId, prompts]);
  
  const handleNext = () => {
    onNext({
      evaluationPromptId: formData.evaluationPromptId,
      evaluationModel: formData.evaluationModel,
      evaluationUseGTSnapshot: formData.evaluationUseGTSnapshot,
      iterations: formData.iterations,
    });
  };
  
  return (
    <Container>
      <StepTitle>평가 설정</StepTitle>
      <StepDescription>
        AI Review 평가에 사용할 프롬프트와 모델을 설정하세요.
      </StepDescription>
      
      <FormGroup>
        <Label>평가 프롬프트 선택</Label>
        {isLoadingPrompts ? (
          <Spinner size="Small" />
        ) : (
          <Dropdown
            defaultValue="평가 프롬프트를 선택하세요"
            selected={
              prompts.find(p => p.id === formData.evaluationPromptId) 
                ? `ID: ${formData.evaluationPromptId} - ${prompts.find(p => p.id === formData.evaluationPromptId)?.version}`
                : '평가 프롬프트를 선택하세요'
            }
            size="Medium"
            active={showPromptDropdown}
            onClick={() => setShowPromptDropdown(!showPromptDropdown)}
          >
            {prompts.map((prompt) => (
              <DropdownMenuItem
                key={prompt.id}
                onClick={() => {
                  setFormData({ ...formData, evaluationPromptId: prompt.id });
                  setShowPromptDropdown(false);
                }}
                isSelected={formData.evaluationPromptId === prompt.id}
              >
                ID: {prompt.id} - {prompt.version} {prompt.description && `(${prompt.description})`}
              </DropdownMenuItem>
            ))}
          </Dropdown>
        )}
        <HelperText>Ground Truth와 AI Review를 비교하여 점수를 매기는 프롬프트입니다.</HelperText>
      </FormGroup>
      
      <FormGroup>
        <Label>평가 모델 선택</Label>
        <Dropdown
          defaultValue="평가 모델을 선택하세요"
          selected={formData.evaluationModel}
          size="Medium"
          active={showModelDropdown}
          onClick={() => setShowModelDropdown(!showModelDropdown)}
        >
          {AVAILABLE_AI_MODELS.map((model) => (
            <DropdownMenuItem
              key={model}
              onClick={() => {
                setFormData({ ...formData, evaluationModel: model });
                setShowModelDropdown(false);
              }}
              isSelected={formData.evaluationModel === model}
            >
              {model}
            </DropdownMenuItem>
          ))}
        </Dropdown>
        <HelperText>평가의 정확도를 위해 고성능 모델 사용을 권장합니다.</HelperText>
        {formData.evaluationPromptId && (
          <HelperText>선택한 평가 프롬프트의 기본 모델이 자동으로 설정되었습니다.</HelperText>
        )}
      </FormGroup>
      
      <FormGroup>
        <Label>반복 횟수</Label>
        <Input
          type="number"
          value={formData.iterations.toString()}
          onChange={(e) => {
            const value = parseInt(e.target.value);
            if (!isNaN(value) && value > 0 && value <= 100) {
              setFormData({ ...formData, iterations: value });
            }
          }}
          placeholder="1-100 사이의 숫자"
          style={{ width: '200px' }}
        />
        <HelperText>동일한 평가를 여러 번 반복하여 일관성을 확인합니다. (최대 100회)</HelperText>
      </FormGroup>
      
      {data.modes?.reevaluateMode && (
        <FormGroup>
          <Label>Ground Truth Snapshot 사용</Label>
          <OptionCard 
            $selected={formData.evaluationUseGTSnapshot}
            onClick={() => setFormData({ 
              ...formData, 
              evaluationUseGTSnapshot: !formData.evaluationUseGTSnapshot 
            })}
          >
            <OptionHeader>
              <CheckBox $selected={formData.evaluationUseGTSnapshot}>
                {formData.evaluationUseGTSnapshot && '✓'}
              </CheckBox>
              <OptionText>기존 평가 시점의 Ground Truth 사용</OptionText>
            </OptionHeader>
            <OptionDescription>
              체크하면 기존 평가 당시에 저장된 Ground Truth를 사용합니다.
              체크하지 않으면 현재의 최신 Ground Truth를 사용합니다.
            </OptionDescription>
          </OptionCard>
        </FormGroup>
      )}
      
      <ButtonGroup>
        <Button
          size="Medium"
          type="Text"
          variant="Ghost"
          color="Secondary"
          onClick={onBack}
        >
          이전
        </Button>
        <Button
          size="Medium"
          type="Text"
          variant="Contained"
          color="Primary"
          onClick={handleNext}
        >
          다음
        </Button>
      </ButtonGroup>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const StepTitle = styled(Text)`
  font-size: 18px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
`;

const StepDescription = styled(Text)`
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  line-height: 1.5;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
`;

const HelperText = styled.span`
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
`;

const OptionCard = styled.div<{ $selected: boolean }>`
  padding: 16px;
  border: 2px solid ${({ $selected }) => 
    $selected ? COLOR_SYSTEM.get("Blue")?.[500] : COLOR_SYSTEM.get("Gray")?.[200]
  };
  border-radius: 8px;
  background-color: ${({ $selected }) => 
    $selected ? COLOR_SYSTEM.get("Blue")?.[50] : 'white'
  };
  cursor: pointer;
  transition: all 0.2s ease;
`;

const OptionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`;

const CheckBox = styled.div<{ $selected: boolean }>`
  width: 20px;
  height: 20px;
  border: 2px solid ${({ $selected }) => 
    $selected ? COLOR_SYSTEM.get("Blue")?.[500] : COLOR_SYSTEM.get("Gray")?.[300]
  };
  border-radius: 4px;
  background-color: ${({ $selected }) => 
    $selected ? COLOR_SYSTEM.get("Blue")?.[500] : 'white'
  };
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
`;

const OptionText = styled(Text)`
  font-size: 14px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
`;

const OptionDescription = styled(Text)`
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  line-height: 1.5;
  padding-left: 32px;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 32px;
`;

export default ExperimentStep4;